import { Application, Feature } from '@frontend/functionality/types';
import { Logger } from '@frontend/logger';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { PackageTriggerName } from '@frontend/package/types';
import { ReviewClient, ReviewTransactionClient } from '@frontend/review/api';
import { ReviewOutcome } from '@frontend/review/types';
import { TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedVendingTransaction, TransactionStateName } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '../../../redux/store';
import { useScreen } from '../../screens/useScreen';

export interface ProductAttribute {
    attributeName: string;
    valueName: string;
}

export interface ProductReview {
    productId: string;
    productName: string;
    attributes: ProductAttribute[];
    expectedCount: number;
    actualCount: number;
    transactions: ExtendedVendingTransaction[];
}

export interface ReviewRecyclesViewProps {
    isLoading: boolean;
    isSubmitting: boolean;
    products: ProductReview[];
    updateActualCount: (productId: string, actualCount: number) => void;
    onSubmit: () => void;
    onBack: () => void;
}

export const useReviewRecycles = (): ReviewRecyclesViewProps => {
    const navigationState = useAppSelector((state) => state.navigation);
    const { onSubmit: onContinue, store, onBack } = useScreen();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [transactions, setTransactions] = useState<ExtendedVendingTransaction[]>([]);
    const [actualCounts, setActualCounts] = useState<Record<string, number>>({});

    const spotId = navigationState.spot?.id;

    useEffect(() => {
        const fetchRecycles = async () => {
            if (!spotId) return;

            const response = await TransactionVendingClient.getRecycles({
                spot_id: spotId,
                state: [TransactionStateName.TRANSACTION_PICK_UP_PENDING],
                size: '1000'
            });

            setTransactions(response.results);
            setIsLoading(false);
        };

        fetchRecycles();
    }, [spotId]);

    const products = useMemo((): ProductReview[] => {
        const grouped = new Map<string, { product: ExtendedVendingTransaction['product']; transactions: ExtendedVendingTransaction[] }>();

        for (const tx of transactions) {
            const productId = tx.product?.entity_id ?? 'unknown';
            const existing = grouped.get(productId);
            if (existing) {
                existing.transactions.push(tx);
            } else {
                grouped.set(productId, { product: tx.product, transactions: [tx] });
            }
        }

        return Array.from(grouped.entries()).map(([productId, { product, transactions: txs }]) => {
            const attributes: ProductAttribute[] = [];
            const data = product?.data;
            if (data?.attributes && typeof data.attributes === 'object') {
                for (const [name, value] of Object.entries(data.attributes)) {
                    attributes.push({ attributeName: name, valueName: String(value) });
                }
            }

            return {
                productId,
                productName: product?.data?.name ?? productId,
                attributes,
                expectedCount: txs.length,
                actualCount: actualCounts[productId] ?? txs.length,
                transactions: txs
            };
        });
    }, [transactions, actualCounts]);

    const updateActualCount = useCallback((productId: string, count: number) => {
        setActualCounts((prev) => ({ ...prev, [productId]: count }));
    }, []);

    const onSubmit = useCallback(async () => {
        const accountId = navigationState.iot?.account_id;
        const userId = navigationState.user?.id;
        if (!accountId || !userId || isSubmitting) return;

        setIsSubmitting(true);

        try {
            for (const product of products) {
                const outcome = product.actualCount === product.expectedCount ? ReviewOutcome.OK : ReviewOutcome.MISSING;

                const review = await ReviewClient.postReview(accountId, {
                    product_id: product.productId,
                    user_id: userId,
                    outcome,
                    expected: product.expectedCount,
                    actual: product.actualCount,
                    application: Application.VENDING,
                    feature: Feature.RECYCLE_TRANSACTION,
                    spot_id: spotId
                });

                await Promise.all(
                    product.transactions.map((tx) =>
                        ReviewTransactionClient.createReviewTransaction(accountId, review.id, {
                            transaction_id: tx.id
                        })
                    )
                );

                await Promise.all(
                    product.transactions.map(async (tx) => {
                        const packages = await PackageClient.fetchAccountTransactionPackages(accountId, tx.id);
                        await Promise.all(
                            packages.results.map((pkg) =>
                                PackageWorkflowClient.triggerPackageState(accountId, tx.id, pkg.id, {
                                    trigger: PackageTriggerName.PACKAGE_AUDITED,
                                    location: navigationState.spot?.id ?? undefined,
                                    actor: navigationState.user?.id ?? undefined
                                })
                            )
                        );
                    })
                );
            }

            const nextStep = store?.steps.find((step) => step.screen === ScreenType.REVIEW_RECYCLES && step.trigger === ScreenTrigger.SUBMIT);
            onContinue(nextStep);
        } catch (error) {
            Logger.error('Failed to submit review recycles', error);
            setIsSubmitting(false);
        }
    }, [navigationState.iot?.account_id, navigationState.user?.id, isSubmitting, products, store, onContinue]);

    return {
        isLoading,
        isSubmitting,
        products,
        updateActualCount,
        onSubmit,
        onBack
    };
};
